import { kebabCase } from 'lodash';
import { pascalCase } from 'utils/pascal-case';
import { unreachable } from 'utils/unreachable';
import { NumberField } from '../fields';
import { TermSeparator } from './separators';

export enum NumberFilterOperationType {
  IsGreaterThan = 'IsGreaterThan',
  IsLessThan = 'IsLessThan',
  Is = 'Is',
  IsNotExactly = 'IsNotExactly',
  IsEmpty = 'IsEmpty',
  IsNotEmpty = 'IsNotEmpty',
}

export const NumberFilterOperationTypes = [
  NumberFilterOperationType.IsGreaterThan,
  NumberFilterOperationType.IsLessThan,
  NumberFilterOperationType.Is,
  NumberFilterOperationType.IsEmpty,
  NumberFilterOperationType.IsNotEmpty,
];

export const isOptional = (type: NumberFilterOperationType) =>
  type === NumberFilterOperationType.IsEmpty ||
  type === NumberFilterOperationType.IsNotEmpty;

interface NumberFieldFilterBase {
  field: NumberField;
}

interface NumberValueFilterBase extends NumberFieldFilterBase {
  value?: number;
}

interface NumberGreaterThanFilter extends NumberValueFilterBase {
  type: NumberFilterOperationType.IsGreaterThan;
}

interface NumberLessThanFilter extends NumberValueFilterBase {
  type: NumberFilterOperationType.IsLessThan;
}

interface NumberEqualToFilter extends NumberValueFilterBase {
  type: NumberFilterOperationType.Is;
}

interface NumberIsNotExactlyFilter extends NumberValueFilterBase {
  type: NumberFilterOperationType.IsNotExactly;
}

interface NumberIsEmptyFilter extends NumberFieldFilterBase {
  type: NumberFilterOperationType.IsEmpty;
}

interface NumberIsNotEmptyFilter extends NumberFieldFilterBase {
  type: NumberFilterOperationType.IsNotEmpty;
}

export type NumberFieldFilter =
  | NumberGreaterThanFilter
  | NumberLessThanFilter
  | NumberEqualToFilter
  | NumberIsNotExactlyFilter
  | NumberIsEmptyFilter
  | NumberIsNotEmptyFilter;

const isValid = (filter: NumberFieldFilter) => {
  switch (filter.type) {
    case NumberFilterOperationType.Is:
    case NumberFilterOperationType.IsNotExactly:
    case NumberFilterOperationType.IsGreaterThan:
    case NumberFilterOperationType.IsLessThan:
      return filter.value != null;
    default:
      return true;
  }
};

const toString = (filter: NumberFieldFilter): string => {
  const type = kebabCase(filter.type);

  switch (filter.type) {
    case NumberFilterOperationType.IsEmpty:
    case NumberFilterOperationType.IsNotEmpty:
      return type;
    case NumberFilterOperationType.IsGreaterThan:
    case NumberFilterOperationType.Is:
    case NumberFilterOperationType.IsNotExactly:
    case NumberFilterOperationType.IsLessThan:
      return [type, filter.value == null ? 'null' : filter.value].join(
        TermSeparator,
      );
    default:
      return unreachable(filter);
  }
};

const parse = (value: string, field: NumberField): NumberFieldFilter => {
  const [type_string, meta] = value.split(TermSeparator);
  const type = pascalCase(type_string) as NumberFilterOperationType;

  switch (type) {
    case NumberFilterOperationType.IsEmpty:
    case NumberFilterOperationType.IsNotEmpty:
      return { field, type };
    case NumberFilterOperationType.IsGreaterThan:
    case NumberFilterOperationType.Is:
    case NumberFilterOperationType.IsNotExactly:
    case NumberFilterOperationType.IsLessThan: {
      const number = JSON.parse(`${meta}`) as number | null;

      return { field, type, value: number == null ? undefined : number };
    }
    default:
      return unreachable(type);
  }
};

export const NumberFieldFilter = {
  isValid,
  toString,
  parse,
};

// TODO: this is a tmp function for determining if number filter needs conditional input;
export const _isConditionalInputNeeded = (
  arg: NumberFieldFilter,
): arg is
  | NumberGreaterThanFilter
  | NumberLessThanFilter
  | NumberEqualToFilter
  | NumberIsNotExactlyFilter =>
  [
    NumberFilterOperationType.Is,
    NumberFilterOperationType.IsNotExactly,
    NumberFilterOperationType.IsGreaterThan,
    NumberFilterOperationType.IsLessThan,
  ].includes(arg.type);
